import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { useToast } from "../hooks/useToast";
import { Bitcoin, TrendingUp, Plus } from "lucide-react";
import { apiRequest } from "../components/lib/queryClient";
import { useAuth } from "../components/features/AuthContext";
import { ApiResponse } from "../components/lib/utils";

const platformSchema = z.object({
    userId: z.string().min(1, "User ID is required"),
    platform: z.string().min(1, "Platform is required"),
    type: z.enum(["crypto", "stocks"]),
    credentials: z.object({
        apiKey: z.string().optional(),
        secretKey: z.string().optional(),
        passphrase: z.string().optional(),
        username: z.string().optional(),
        password: z.string().optional(),
        accountId: z.string().optional(),
        clientId: z.string().optional(),
        accessToken: z.string().optional(),
    }),
});


type PlatformFormData = z.infer<typeof platformSchema>;
const platformConfigs = {
    binance: {
        name: "Binance",
        icon: Bitcoin,
        color: "bg-yellow-500",
        fields: ["apiKey", "secretKey", "passphrase"],
        type: "crypto",
    },
    // zerodha: {
    //     name: "Zerodha",
    //     icon: TrendingUp,
    //     color: "bg-blue-600",
    //     fields: ["apiKey", "clientId"],
    //     type: "stocks",
    // },
    dhan: {
        name: "Dhan",
        icon: TrendingUp,
        color: "bg-green-600",
        fields: ["clientId", "accessToken"],
        type: "stocks",
    },
};

export default function PlatformIntegration() {
    const [selectedPlatform, setSelectedPlatform] = useState<string>("");
    const [editingIntegration, setEditingIntegration] = useState<any>(null);
    const { user, currentTradingMode, isCryptoEnabled, isStocksEnabled } = useAuth()
    const { toast } = useToast();
    const queryClient = useQueryClient();


    // Fetch integrations, refetch when the user or mode changes
    const {
        data: integrations = [],
        isLoading,
        isError,
        error,
    } = useQuery<PlatformFormData[]>({
        queryKey: [
            "api/platform-integrations",
            user?.userId,
            currentTradingMode,
        ],
        queryFn: async () => {
            if (!user?.userId || !currentTradingMode) return [];
            const res = await apiRequest<ApiResponse<{ integrations: PlatformFormData[] }>>(
                "GET",
                `api/platform-integrations?userId=${user.userId}&category=${currentTradingMode}`
            );
            console.log("res",res)
            return res.data.integrations;
        },
        enabled: !!user?.userId && !!currentTradingMode,
    });

    // Form setup: userId and type come from auth/context
    const form = useForm<PlatformFormData>({
        resolver: zodResolver(platformSchema),
        defaultValues: {
            userId: user?.userId || "",
            platform: "",
            type: currentTradingMode,
            credentials: {},
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: PlatformFormData) =>
            apiRequest("POST", "api/platform-integrations", data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["api/platform-integrations", user?.userId, currentTradingMode],
            });
            form.reset({ userId: user?.userId || "", platform: "", type: currentTradingMode, credentials: {} });
            setSelectedPlatform("");
            toast({ title: "Success", description: "Platform connected successfully" });
        },
        onError: () => {
            toast({ title: "Error", description: "Failed to connect platform", variant: "destructive" });
        },
    });

    // Update existing integration
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<PlatformFormData> }) =>
            apiRequest("PUT", `api/platform-integrations/${id}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["api/platform-integrations", user?.userId, currentTradingMode],
            });
            setEditingIntegration(null);
            form.reset({ userId: user?.userId || "", platform: "", type: currentTradingMode, credentials: {} });
            setSelectedPlatform("");
            toast({ title: "Success", description: "Platform updated successfully" });
        },
        onError: () => {
            toast({ title: "Error", description: "Failed to update platform", variant: "destructive" });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (platformKey: string) => {
            return apiRequest(
                "DELETE",
                `api/platform-integrations?userId=${user?.userId}&platform=${platformKey}&category=${currentTradingMode}`
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["api/platform-integrations", user?.userId, currentTradingMode],
            });
            toast({
                title: "Success",
                description: "Platform disconnected successfully",
            });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to disconnect platform",
                variant: "destructive",
            });
        },
    });

    const onSubmit = (data: PlatformFormData) => {
        data.type = currentTradingMode
        if (editingIntegration) {
            updateMutation.mutate({ id: editingIntegration._id, data });
        } else {
            createMutation.mutate(data);
        }
    };

    const handleEdit = (integration: any) => {
        setEditingIntegration(integration);
        setSelectedPlatform(integration.platform);

        form.reset({
            userId: integration.userId,
            platform: integration.platform,
            type: integration.type,
            credentials: integration.credentials || {},
        });
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-64 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">

            {/* Add New Platform */}
            {(selectedPlatform || editingIntegration) && (
                <Card className="mb-6">
                    <CardContent className="pt-6">
                        <h3 className="font-medium text-gray-900 mb-4">
                            {editingIntegration ? "Edit Platform" : "Connect New Platform"}
                        </h3>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                {!editingIntegration && (
                                    <FormField
                                        control={form.control}
                                        name="platform"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Select Platform</FormLabel>
                                                <Select onValueChange={(value: string) => {
                                                    field.onChange(value);
                                                    setSelectedPlatform(value);
                                                }} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Choose a platform" />
                                                        </SelectTrigger>
                                                    </FormControl>

                                                    <SelectContent>
                                                        {Object.entries(platformConfigs)
                                                            .filter(([_, config]) => config.type === currentTradingMode)
                                                            .map(([key, config]) => {
                                                                const Icon = config.icon;
                                                                return (
                                                                    <SelectItem key={key} value={key} className="flex flex-row items-center gap-2">
                                                                        {/* <Icon className="w-4 h-4 text-muted-foreground" /> */}
                                                                        {config.name}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                {(selectedPlatform || editingIntegration) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {platformConfigs[selectedPlatform as keyof typeof platformConfigs]?.fields.map((fieldName) => (
                                            <FormField
                                                key={fieldName}
                                                control={form.control}
                                                name={`credentials.${fieldName}` as any}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>
                                                            {fieldName === "apiKey" ? "API Key" :
                                                                fieldName === "secretKey" ? "Secret Key" :
                                                                    fieldName === "passphrase" ? "Passphrase" :
                                                                        fieldName === "username" ? "Username" :
                                                                            fieldName === "password" ? "Password" :
                                                                                fieldName === "accountId" ? "Account ID" : fieldName}
                                                        </FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                type={fieldName === "password" || fieldName === "secretKey" ? "password" : "text"}
                                                                placeholder={`Enter ${fieldName}`}
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        ))}
                                    </div>
                                )}

                                <div className="flex space-x-3">
                                    <Button
                                        type="submit"
                                        disabled={createMutation.isPending || updateMutation.isPending}
                                    >
                                        {editingIntegration ? "Update" : "Save"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setSelectedPlatform("");
                                            setEditingIntegration(null);
                                            form.reset();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </CardContent>
                </Card>
            )}

            {/* Platform Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {integrations && integrations.map((integration: any) => {
                    const config = platformConfigs[integration.platform as keyof typeof platformConfigs];
                    if (!config) return null;

                    const IconComponent = config.icon;

                    return (
                        <Card key={integration.platform} className="border border-gray-200">
                            <CardContent className="pt-6">
                                <div className="flex items-center space-x-3 mb-4">
                                    <div className={`w-12 h-12 ${config.color} rounded-lg flex items-center justify-center`}>
                                        <IconComponent className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{config.name}</h4>
                                        <p className="text-sm text-gray-500">
                                            {config.type === "crypto" ? "Cryptocurrency Exchange" : "Stock Trading"}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-3 mb-4">
                                    <div className="text-sm text-gray-600">
                                        Connected :
                                        {new Date(integration.updatedAt).toLocaleDateString("en-US", {
                                            day: "2-digit",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </div>
                                    {integration.lastSync && (
                                        <div className="text-sm text-gray-600">
                                            Last sync: {new Date(integration.lastSync).toLocaleString()}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-between">
                                    <div className="flex items-center text-sm">
                                        <div className={`w-2 h-2 rounded-full mr-2 ${integration.isConnected ? "bg-success" : "bg-error"
                                            }`}></div>
                                        {integration.isConnected ? "Connected" : "Disconnected"}
                                    </div>
                                    <div className="flex space-x-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleEdit(integration)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => deleteMutation.mutate(integration.platform)}
                                            disabled={deleteMutation.isPending}
                                        >
                                            Disconnect
                                        </Button>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}

                {/* Add New Platform Card */}
                {!selectedPlatform && !editingIntegration && (
                    <Card className="border border-gray-200 border-dashed">
                        <CardContent className="pt-6">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                                    <Plus className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">Add Platform</h4>
                                    <p className="text-sm text-gray-500">Connect another exchange</p>
                                </div>
                            </div>

                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                    currentTradingMode == "crypto" ? setSelectedPlatform("binance") : setSelectedPlatform("dhan")
                                }
                                }
                            >
                                Configure Platform
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
